import cn from 'clsx'
import Pane from 'components/Common/Pane'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import OfferPageGallery from '../OfferPageGallery'
import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import {
  fetchOfferFlightPrice,
  fetchRelatedTravelItems,
} from 'actions/OfferActions'
import { CLEAR_OFFER_CHECK_IN_CHECK_OUT, SET_OFFER_CHECK_IN_CHECK_OUT } from 'actions/actionConstants'
import Breakpoint from 'components/Common/Breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import WalledContent from 'components/Common/WalledContent'
import BodyText from 'LuxKit/Typography/BodyText'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import Backdrop from 'components/utils/Backdrop'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { replace } from 'connected-react-router'
import config from 'constants/config'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { urlHashDatePicker } from 'constants/url'
import { OfferPageActions, OfferPageFilterType, OfferPageRoom, initialOfferPageRooms } from 'contexts/OfferPage/offerPageStateReducer'
import useFlightPrice from 'hooks/useFlightPrice'
import useMountedEffect from 'hooks/useMountedEffect'
import usePrevious from 'hooks/usePrevious'
import usePropertyRates from 'hooks/usePropertyRates'
import {
  countOccupantsForFlights,
  isValidFlightsOccupants,
} from 'lib/offer/occupancyUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import offerPageURL from 'lib/offer/offerPageURL'
import { hasOfferExpired } from 'lib/offer/offerUtils'
import { encodeSearchParams } from 'lib/search/searchUtils'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import OfferAirportSelect from '../Filters/Airport/OfferAirportSelect'
import OfferAirportSelectBox from '../Filters/Airport/OfferAirportSelectBox'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferDatePickerSelect from '../Filters/DatePicker/OfferDatePickerSelect'
import OfferDatePickerSelectBox from '../Filters/DatePicker/OfferDatePickerSelectBox'
import OfferDurationTabs from '../Filters/DurationTabs/OfferDurationTabs'
import AlwaysOnSidebarFilters from '../AlwaysOn/AlwaysOnSidebarFilters'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import OfferYmalStickyFooter from 'components/Recommendations/YouMayAlsoLike/Offer/OfferYmalStickyFooter'
import { InViewHookResponse, useInView } from 'react-intersection-observer'
import OfferPageFlightDealBanner from '../OfferPageFlightDealBanner'
import ModalProvider from 'contexts/ModalProvider'
import ContentTagBanner from 'components/Content/ContentTagBanner'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import SavePromptHotel from 'tripPlanner/components/Bookmark/SavePrompt/SavePromptHotel'
import useShareModalConfig from 'hooks/useShareModalConfig'
import moment from 'moment'
import PropertyRoomOptions from './PropertyRoomOptions'
import PropertyStickyFooter from './PropertyStickyFooter'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import PropertyIntro from './PropertyIntro'
import PropertyStickyHeader from './PropertyStickyHeader'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import AccommodationOfferPageStickyFooter from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyFooter'
import SinglePropertyHotelFlightsToggleButton from '../Accommodation/HotelFlightsToggle/SinglePropertyHotelFlightsToggleButton'
import AccommodationOfferPageStickyHeaderMobile from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyHeaderMobile'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import {
  getCheckoutDate,
  getPropertyDestinationAirportCode,
  getPropertyLoggedOutSavedPercent,
  getPropertyRatesKey,
  getShouldShowPropertyAnytimeRate,
  isPropertyAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useQsQueryParams } from 'hooks/useQueryParams'
import { useLocation } from 'react-router'
import GeoContext from 'contexts/geoContext'
import AlwaysOnMobileStickyHeaderFilters from '../AlwaysOn/MobileBookingFlow/AlwaysOnMobileStickyHeaderFilters'
import useOffer from 'hooks/Offers/useOffer'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'

const RelativeBlock = styled.form`
  position: relative;
`

const PackageOptionsPane = styled(Pane)`
  padding: ${rem(20)} 0;
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.no-padding {
    padding: 0;
  }
`

const FlexGroup = styled(Group)`
  flex-grow: 1;
`

const FlexDesktopFilterBlock = styled(DesktopFilterBlock)`
  flex-grow: 1;
`

interface Props {
  shouldUseLereStickyFooter: boolean
  lereYmalStickyFooterShowTrigger: InViewHookResponse
  shouldUseAccomContent: boolean
  property: App.Property.Property
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  offer: App.HotelOffer
  airport?: App.Airport
  destinationAirportCode: string | undefined
  duration: number
  activeFilter?: OfferPageFilterType
  activeHeaderFilter?: OfferPageFilterType
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  checkOutDate?: string
  flights?: boolean
  areAnytimeRates: boolean
  shouldShowPropertyAnytimeRates: boolean
  availableForPurchase?: boolean
  loggedOutSavedPercent: number | undefined
  room?: App.PackageRoomType
  regionCode: string
  utmContent: string | undefined
}

const currentTimer = {} as Record<string, any>

const slideTimer = (name: string, callback: any, ms: number) => {
  if (currentTimer[name]) {
    clearTimeout(currentTimer[name])
  }
  currentTimer[name] = setTimeout(callback, ms)
}

function PropertyContent(props: Props) {
  const {
    shouldUseLereStickyFooter,
    lereYmalStickyFooterShowTrigger,
    shouldUseAccomContent,
    property,
    offer,
    airport,
    destinationAirportCode,
    duration,
    activeFilter,
    rooms,
    checkInDate,
    checkOutDate,
    flights,
    areAnytimeRates,
    shouldShowPropertyAnytimeRates,
    availableForPurchase,
    loggedOutSavedPercent,
    activeHeaderFilter,
    regionCode,
    utmContent,
  } = props

  const location = useLocation()
  const query = useQsQueryParams<{ roomRateId?: string, roomTypeId?: string, roomOptionRateId?: string }>()
  const { originAirports, airports } = useContext(GeoContext)
  const prevPath = useAppSelector(state => state.routeHistory.prevPath)

  const dispatch = useAppDispatch()
  const stateDispatch = useOfferPageDispatch()

  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousRooms = usePrevious(rooms)
  const previousCheckIn = usePrevious(checkInDate)
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.Occupants>>([])
  const desktopFilterBlockRef = useRef<HTMLDivElement>(null)

  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()

  const isMobileDevice = useScreenSizeOnly('mobile')
  // NB: This experiment is currently disabled, and should _not_ be re-enabled until we've rewritten
  // all the mobile flow components.
  const isNewMobileBookingFlowEnabled = useOptimizelyExperiment(OptimizelyExperiments.croAlwaysOnNewMobileBooking, isMobileDevice)
  const useNewMobileBookingFlow = isMobileDevice && isNewMobileBookingFlowEnabled

  const areFlightsEnabled = shouldUseAccomContent ? property.areFlightsEnabled : offer.offerFlightsEnabled
  const showHotelAndFlightsToggle = !offer.bundledWithFlightsOnly && areFlightsEnabled
  const showDesktopHotelAndFlightsToggle = !isMobileDevice && showHotelAndFlightsToggle

  const queryString = useMemo(() =>
    encodeSearchParams({
      urlSearch: location.search,
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms,
    }).toString(), [checkInDate, checkOutDate, rooms, location.search])

  // Need to pass the context state above the tree
  useEffect(() => {
    dispatch({
      type: SET_OFFER_CHECK_IN_CHECK_OUT,
      payload: {
        offerId: offer.id,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        duration,
        rooms,
      },
    })

    return
  }, [dispatch, offer.id, checkInDate, duration, rooms, checkOutDate])

  useEffect(() => {
    // clear the check in/check out on unmount
    return () => {
      dispatch({
        type: CLEAR_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: offer.id,
        },
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useMountedEffect(() => {
    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
      previousRooms !== rooms ||
      previousCheckIn !== checkInDate
  }, [rooms, checkInDate])

  useIdleTimeReporter(IdleTimeSource.AlwaysOn)

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => packageOptionsAnchor.current?.scrollIntoView(), [])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToFilters = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      requestAnimationFrame(() => {
        desktopFilterBlockRef.current?.scrollIntoView({ behavior: 'auto' })
      })
    }
  }, [isTabletScreenSizeAndUp])

  const hasRoomsChanged = useMemo(() => {
    const currentOccupancyNumber = rooms.map(generateOccupancyStringByRoom).join('|')
    const lastOccupancyNumber = (lastFetchedRooms.current ?? initialOfferPageRooms).map(generateOccupancyStringByRoom).join('|')
    return currentOccupancyNumber !== lastOccupancyNumber
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, activeFilter])

  useEffect(() => {
    const hasRooms = hasSelectedRooms(rooms)
    if (previousFilter === 'capacity' && !hasRooms) {
      if (!activeFilter) {
        autoScrollToFilters()
        scrollAfterFilterClose.current = false
      }
    }

    if (activeFilter) {
      autoScrollToFilters()
    }
  }, [activeFilter, activeHeaderFilter, previousFilter, hasRoomsChanged]) // eslint-disable-line react-hooks/exhaustive-deps

  const roomOption = useMemo(() => {
    if (!query.roomTypeId) return
    // Typically, `roomRateId` is for `gha-lpc` and `roomOptionRateId` is for `gha-lpp`.
    const roomRateId = query.roomRateId || query.roomOptionRateId
    if (!roomRateId) return
    return { roomTypeId: query.roomTypeId, roomRateId }
  }, [query.roomTypeId, query.roomRateId, query.roomOptionRateId])
  useEffect(() => {
    stateDispatch({ type: OfferPageActions.setRoomOption, roomOption })
  }, [stateDispatch, roomOption])

  useEffect(() => {
    slideTimer('fetchRelatedTravelItems', () => {
      dispatch(fetchRelatedTravelItems(offer, checkInDate, checkOutDate, rooms))
    }, 2000)
  }, [offer, duration, checkInDate, dispatch, rooms, checkOutDate])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (!activeFilter) {
      autoScrollToFilters()
    }
  }, [duration])

  useMountedEffect(() => {
    // effectively an 'on airport change' event
    if (airport) {
      dispatch(setRecentlyUsedAirportCode(airport.code))
      dispatch(fetchOfferFlightPrice(offer.id, airport.code))
    }
  }, [airport])

  useEffect(() => {
    if (location.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(replace({ ...location, search: queryString }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  // Get the mapping for this property page so we know what bedbank offer to fetch
  const [mapping] = usePropertyOfferMapping(OFFER_TYPE_ALWAYS_ON, offer.id)
  // Get the bedbank offer for this property mapping
  const [bedbankOffer] = useOffer<App.BedbankOffer>(mapping?.bedbankPropertyIds?.[0])

  // Ensure property rates are loaded.
  usePropertyRates({
    offerType: OFFER_TYPE_ALWAYS_ON,
    offerId: offer.id,
    checkIn: checkInDate,
    checkOut: checkOutDate,
    occupancies: rooms,
    utmContent,
    roomTypeId: roomOption?.roomTypeId,
    roomRateId: roomOption?.roomRateId,
  })
  // Ensure cheapest flights are also loaded. Note that (for now) we'll assume: (i) both LPC and
  // bedbank offers are configured identically (i.e. both have flights enabled and the same
  // destination airport code); and (ii) there's no `forceBundleId` for LPC offers (or its discount
  // is immaterial).
  useFlightPrice(
    destinationAirportCode,
    rooms,
    checkInDate,
    checkOutDate,
    airport?.code,
    duration,
  )

  const occupancyLimits = useMemo(() => {
    if (shouldUseAccomContent) {
      return Object.values(property.rooms).flatMap(({ capacities }) => capacities)
    }
    const limits = offer.packages.flatMap((p) => p.roomRate.capacities)
    const lppLimits = bedbankOffer?.packages.flatMap((p) => p.capacities.combinations) ?? []
    return [
      ...limits,
      ...lppLimits,
    ]
  }, [shouldUseAccomContent, property.rooms, offer.packages, bedbankOffer?.packages])

  const isValidFlightsOccupancy = useMemo(() => {
    const flightOccupants = countOccupantsForFlights(rooms)
    return isValidFlightsOccupants(flightOccupants)
  }, [rooms])
  // TODO(HP-1495): Plumb flag through accommodation service property content endpoint.
  const shouldShowFlights = offer.bundledWithFlightsOnly || isValidFlightsOccupancy
  const destinationAirport = useMemo(() => airports.find(({ code }) => code === destinationAirportCode), [airports, destinationAirportCode])
  const showFlightInformation = destinationAirport &&
    (originAirports.length > 1 || (originAirports.length > 0 && destinationAirport.code !== airport?.code)) &&
    shouldShowFlights

  const durationOptionsOverride = shouldUseAccomContent ? property.durationOptions : undefined
  const onBeforeDurationClick = useCallback((duration: number) => {
    if (!(areAnytimeRates && shouldShowPropertyAnytimeRates)) return true
    if (duration !== property.marketedLos) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      return false
    }
    return true
  }, [areAnytimeRates, property.marketedLos, shouldShowPropertyAnytimeRates, stateDispatch])

  // ------------- LERE sticky footer -------------------
  const recommendationFilters = useMemo(() => ({
    checkIn: checkInDate,
    checkOut: checkInDate ? moment(checkInDate).clone().add(duration, 'days').format(ISO_DATE_FORMAT) : undefined,
    rooms,
  }), [checkInDate, duration, rooms])

  const lereYmalStickyFooterHideTrigger = useInView({ triggerOnce: false })

  const canShowYmalFooter = lereYmalStickyFooterShowTrigger.inView && !lereYmalStickyFooterHideTrigger.inView
  const showDealBanner = airport && destinationAirport

  // ------------- END of LERE sticky footer -------------------

  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)

  const shareConfig = useShareModalConfig({
    image: offer.images[0],
    offerId: offer.id,
    location: [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', '),
    type: 'offer',
    utmCampaign: 'webshare',
    queryString,
    offerType: 'hotel',
  })

  // Do not show the Expired recommendation when it comes from my escapes
  const shouldShowLere = useMemo(() => hasOfferExpired(offer) && !prevPath.includes('/my-escapes/order'), [offer, prevPath])

  const bedbankOfferPath = useMemo(() => {
    // Keep this condition consistent with `fetchBedbankOfferAndBreadcrumbs.ts` and `BedbankOfferPage.tsx`.
    if (!bedbankOffer?.promotions.length) return
    return `/${regionCode.toLowerCase()}${offerPageURL(bedbankOffer)}`
  }, [bedbankOffer, regionCode])
  if (bedbankOfferPath) {
    // Do _not_ perform a permanent redirect; we only need this temporary redirect fallback until we
    // notice the misconfiguration and disable the SPP. When the bedbank promotions expire, we'll
    // re-enable the SPP.
    return <RedirectWithStatus code={302} to={bedbankOfferPath} />
  }

  return <ProductPaletteProvider product={offer}>
    <ImmersiveSaveButtonPortal>
      <BookmarkButton offer={offer} />
    </ImmersiveSaveButtonPortal>
    <TripGuard>
      <SavePromptHotel offer={offer} />
    </TripGuard>
    <RelativeBlock onSubmit={(e) => e.preventDefault()}>
      {useNewMobileBookingFlow && <AccommodationOfferPageStickyHeaderMobile>
        <AlwaysOnMobileStickyHeaderFilters />
      </AccommodationOfferPageStickyHeaderMobile>}
      <div>
        <CSSBreakpoint min="tablet">
          <PropertyStickyHeader
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
          />
        </CSSBreakpoint>
        {shouldShowLere && (
          <div>
            <ExpiredDealsLereRecommendations hideOffers={!!activeFilter} />
          </div>
        )}
        {availableForPurchase && (
          <WalledContent enforced={offer.walledGarden}>
            <Breakpoint max="mobile">
              <ModalProvider>
                <AlwaysOnSidebarFilters
                  onViewPackageOptions={scrollToPackageOptions}
                  capacities={occupancyLimits}
                  shouldShowCalendarWithoutPricing
                />
              </ModalProvider>
            </Breakpoint>
          </WalledContent>
        )}
        <span ref={lereYmalStickyFooterHideTrigger.ref} />
        <OfferPageGallery offer={offer} shareModalConfig={shareConfig} autoPlayVideo={!!offer.isVideoVimeoHero} />
        <Pane>
          <PropertyIntro
            property={property}
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
            shareConfig={shareConfig}
          />
        </Pane>
      </div>
      <ScrollAnchor ref={packageOptionsAnchor} />
      <SoftOfferPageWalledGarden>
        <span ref={lereYmalStickyFooterShowTrigger.ref} />
        <PackageOptionsPane id="Package-Options">
          <StyledLayoutContainer className={cn({ 'no-padding': useNewMobileBookingFlow })}>
            <VerticalSpacer gap={32}>
              {availableForPurchase && (
                <WalledContent enforced={offer.walledGarden}>
                  <>
                    {!useNewMobileBookingFlow && (
                      <CSSBreakpoint max="mobile">
                        <Group direction="vertical" gap={16}>
                          {/* We've temporarily disabled `cro_lpc_new_mobile_booking`, so we're on
                          the old mobile flow. However, we're not showing hotel-only and
                          hotel-with-flights pricing side-by-side, so we need this toggle to be
                          present. */}
                          <SinglePropertyHotelFlightsToggleButton />
                          <Group direction="vertical" gap={8}>
                            <OfferCapacityWithAgesSelectBox />
                            <OfferDatePickerSelectBox />
                            {showFlightInformation && flights && <OfferAirportSelectBox />}
                          </Group>
                        </Group>
                      </CSSBreakpoint>
                    )}
                    <CSSBreakpoint min="tablet">
                      <ScrollAnchor ref={desktopFilterBlockRef} vOffset={rem(12)} />
                      {showDesktopHotelAndFlightsToggle && (
                        <Group ref={desktopFiltersWrapperRef} fullWidth direction="horizontal" gap={8} horizontalAlign="space-between" wrap="wrap">
                          <FlexGroup direction="horizontal" horizontalAlign="center" desktopHorizontalAlign="start">
                            <SinglePropertyHotelFlightsToggleButton />
                          </FlexGroup>
                          <FlexDesktopFilterBlock direction="horizontal" gap={8} horizontalAlign="center" desktopHorizontalAlign="end" wrap="wrap" className={cn({ open: !!activeFilter })}>
                            {showFlightInformation && flights && <OfferAirportSelect />}
                            <OfferCapacityWithAgesSelect boundaryRef={desktopFiltersWrapperRef} capacities={occupancyLimits} />
                            <OfferDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />
                            <Backdrop visible={!!activeFilter && !isMobileDevice} data-testid="filter-backdrop" />
                          </FlexDesktopFilterBlock>
                        </Group>
                      )}
                      {!showDesktopHotelAndFlightsToggle && (
                        <DesktopFilterBlock ref={desktopFiltersWrapperRef} direction="horizontal" gap={8} horizontalAlign="center" className={cn({ open: !!activeFilter })}>
                          <OfferCapacityWithAgesSelect boundaryRef={desktopFiltersWrapperRef} capacities={occupancyLimits} />
                          <OfferDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />
                          {showFlightInformation && <OfferAirportSelect />}
                          <Backdrop visible={!!activeFilter && !isMobileDevice} data-testid="filter-backdrop" />
                        </DesktopFilterBlock>
                      )}
                    </CSSBreakpoint>
                  </>
                </WalledContent>
              )}

              {!useNewMobileBookingFlow && (
                <VerticalSpacer gap={8}>
                  <BodyText variant="large" align="center">
                    Select duration
                  </BodyText>
                  <OfferDurationTabs
                    durationOptionsOverride={durationOptionsOverride}
                    onBeforeDurationClick={onBeforeDurationClick}
                    availableForPurchaseOverride={availableForPurchase}
                  />
                </VerticalSpacer>
              )}

              <LayoutContainer>
                <VerticalSpacer gap={16}>
                  <ContentTagBanner offerId={offer.id} page="offer" />
                  {showDealBanner && config.FLIGHT_ENABLED && <OfferPageFlightDealBanner
                    destinationAirportCode={destinationAirport.code}
                    originAirportCode={airport.code}
                    locationHeadingOverride={shouldUseAccomContent ?
                      // TODO(HP-1495): Expose title without subheading if we want to be pedantic.
                      property.location.title :
                      undefined}
                  />}
                </VerticalSpacer>
              </LayoutContainer>
              {offer.packages.length > 0 && !useNewMobileBookingFlow && <PropertyRoomOptions
                showFlightInformation={showFlightInformation}
                destinationAirport={destinationAirport}
                onViewPackageOptions={scrollToPackageOptions}
              />}
            </VerticalSpacer>
          </StyledLayoutContainer>
        </PackageOptionsPane>
        {!useNewMobileBookingFlow && availableForPurchase && (!shouldUseLereStickyFooter || !canShowYmalFooter) &&
          <PropertyStickyFooter
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
          />}
        {useNewMobileBookingFlow && availableForPurchase && <AccommodationOfferPageStickyFooter />}
      </SoftOfferPageWalledGarden>
    </RelativeBlock>
    <SoftOfferPageWalledGarden bypassWall={isWalledGardenEuropeOffersEnabled} wall={<OfferPageSignUpWall offerType="hotel" saveUpToPercent={loggedOutSavedPercent} />} />
    {shouldUseLereStickyFooter && !useNewMobileBookingFlow && (
      <ErrorBoundary fallback={null}>
        <OfferYmalStickyFooter
          seedOffer={offer}
          filters={recommendationFilters}
          show={canShowYmalFooter}
          initialShowDelayMs={2000}
          hideImmediately={!!activeFilter}
          pageSource="AlwaysOn"
        />
      </ErrorBoundary>
    )}
  </ProductPaletteProvider>
}

type MappedProps = Pick<Props,
  | 'shouldUseAccomContent'
  | 'airport'
  | 'destinationAirportCode'
  | 'duration'
  | 'activeFilter'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'flights'
  | 'room'
  | 'areAnytimeRates'
  | 'shouldShowPropertyAnytimeRates'
  | 'availableForPurchase'
  | 'loggedOutSavedPercent'
  | 'activeHeaderFilter'
  | 'regionCode'
  | 'utmContent'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  shouldUseAccomContent: appState.system.features.flags[OptimizelyFeatureFlags.sppUseAccomContent],
  airport: state.airport,
  destinationAirportCode: getPropertyDestinationAirportCode(state, appState),
  duration: state.duration,
  activeFilter: state.activeFilter,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
  flights: state.flights,
  room: state.room,
  areAnytimeRates: !getPropertyRatesKey(state, appState),
  shouldShowPropertyAnytimeRates: getShouldShowPropertyAnytimeRate(state, appState),
  availableForPurchase: isPropertyAvailableForPurchase(state, appState),
  loggedOutSavedPercent: getPropertyLoggedOutSavedPercent(state, appState),
  activeHeaderFilter: state.activeHeaderFilter,
  regionCode: appState.geo.currentRegionCode,
  utmContent: appState.router.location.query.utm_content,
}), PropertyContent)
